import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { PromptDataProfileCopyEntity } from "../../types/PromptDataProfileCopyEntity";
import type { PromptDataProfileEntity } from "../../types/PromptDataProfileEntity";
import { PromptDataProfileCopyEntityToCopyDto } from "../mappers/PromptDataProfileCopyEntityToCopyDto";
import { PromptDataProfileDtoToEntity } from "../mappers/PromptDataProfileDtoToEntity";
import { PromptDataProfileEntityToDto } from "../mappers/PromptDataProfileEntityToDto";

const PromptDataProfileApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        PromptDataProfileLoad: builder.query<PromptDataProfileEntity, FormQuery>({
            providesTags: [ResourceName.PROMPT_DATA_PROFILES],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PROMPT_DATA_PROFILES}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return PromptDataProfileDtoToEntity(response);
            },
        }),
        PromptDataProfileSave: builder.mutation<PromptDataProfileEntity, FormMutation<PromptDataProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PROMPT_DATA_PROFILES}`;
                const method = "POST";

                const body = PromptDataProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.PROMPT_DATA_PROFILES],
        }),
        PromptDataProfileUpdate: builder.mutation<PromptDataProfileEntity, FormMutation<PromptDataProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PROMPT_DATA_PROFILES}/${mutation.data.id}`;
                const method = "PUT";

                const body = PromptDataProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.PROMPT_DATA_PROFILES],
        }),
        PromptDataProfileCopy: builder.mutation<PromptDataProfileEntity, FormMutation<PromptDataProfileCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PROMPT_DATA_PROFILES}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = PromptDataProfileCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.PROMPT_DATA_PROFILES],
        }),
    }),
});

export const {
    usePromptDataProfileLoadQuery,
    usePromptDataProfileSaveMutation,
    usePromptDataProfileUpdateMutation,
    usePromptDataProfileCopyMutation,
} = PromptDataProfileApi;
